import React, { useEffect, useRef } from 'react';
import { Bot, UserRound } from 'lucide-react';
import { characterFor } from './characters';

// AUTO-WA-39: the main app's AUTO-319 WorkspaceUi helpers, plus the page
// header every WhatsApp page now shares so sections stop looking different.

export function PageHeader({ eyebrow, title, description, actions, children }) {
  return (
    <header className="ws-header wa-page-header">
      <div className="ws-grow">
        {eyebrow && <div className="ws-eyebrow">{eyebrow}</div>}
        <h1 className="ws-title" style={{ marginTop: eyebrow ? 6 : 0 }}>{title}</h1>
        {description && <p className="ws-muted wa-page-lede">{description}</p>}
        {children}
      </div>
      {actions && <div className="wa-page-actions">{actions}</div>}
    </header>
  );
}

// A full-width Stage page. `wide` removes the 1320px cap for tables.
export function StagePage({ children, className = '', wide = false, label }) {
  return (
    <main className={`ws ${className}`} aria-label={label}>
      <div className="ws-page" style={wide ? { maxWidth: 1480 } : undefined}>{children}</div>
    </main>
  );
}

export function AgentAvatar({ characterKey, size = 36, name = '' }) {
  const character = characterFor(characterKey);
  const radius = Math.round(size * 0.3);
  if (!character) {
    return (
      <span className="ws-avatar ws-avatar--blank" style={{ width: size, height: size, borderRadius: radius }} aria-hidden="true">
        <Bot size={Math.max(14, Math.round(size * 0.44))} strokeWidth={1.9} />
      </span>
    );
  }
  return (
    <span className="ws-avatar" style={{ width: size, height: size, borderRadius: radius, background: character.tint }}>
      <img src={character.image} alt={name ? `${name}'s character` : ''} loading="lazy" />
    </span>
  );
}

export function CharacterStage({ character, height = 188, children }) {
  return (
    <div className="ws-stage" style={{ height, background: character?.tint || undefined }}>
      {character && <span className="ws-stage-glow" aria-hidden="true" />}
      {character?.full && <img className="ws-stage-full" src={character.full} alt="" />}
      {character && !character.full && <img className="ws-stage-face" src={character.image} alt="" />}
      {children}
    </div>
  );
}

// AUTO-WA-39: every person gets a colour from the first letter of their name,
// so lists feel alive and people are easy to tell apart. The most common first
// letters each have their own colour; rarer letters share. Brand-safe hues only
// (no purple or indigo), dark enough at the bottom for white initials.
const TONES = {
  blue: ['#3AAEE8', '#1478AE'], navy: ['#3F7196', '#183F5A'], teal: ['#2EC4B6', '#0E8378'],
  green: ['#3CC983', '#1A8A4B'], lime: ['#9ACD4B', '#5A8816'], olive: ['#BDBF4A', '#7C8119'],
  amber: ['#F2B53C', '#B87605'], orange: ['#FA9D4E', '#CF5F14'], coral: ['#F7866B', '#CC4A34'],
  red: ['#EE6F6F', '#B83838'], rose: ['#F27AA0', '#C2416E'], cinnamon: ['#D6A06E', '#99613A'],
  cyan: ['#4CCFE5', '#12889F'], sky: ['#7CC4E8', '#3A8CBF'], slate: ['#8CA1B2', '#51697A'], forest: ['#4FA873', '#256B42'],
};
const LETTER_TONES = {
  S: 'lime', A: 'blue', R: 'cinnamon', M: 'red', P: 'cyan', K: 'navy', N: 'green', D: 'amber', V: 'forest', G: 'teal',
  H: 'rose', J: 'sky', B: 'coral', T: 'orange', C: 'olive', L: 'slate',
  E: 'sky', F: 'green', I: 'orange', O: 'blue', Q: 'amber', U: 'coral', W: 'teal', X: 'cyan', Y: 'red', Z: 'navy',
};
const TONE_ORDER = Object.keys(TONES);

export function monogramTone(name = '') {
  const letter = String(name || '').trim().charAt(0).toUpperCase();
  const tone = LETTER_TONES[letter] || (letter ? TONE_ORDER[letter.codePointAt(0) % TONE_ORDER.length] : 'slate');
  const [from, to] = TONES[tone];
  return { tone, from, to, background: `linear-gradient(145deg, ${from}, ${to})` };
}

// Initials come from words that start with a letter; a phone number alone shows a person icon.
export function Monogram({ name = '', size = 32 }) {
  const words = String(name || '').split(/\s+/).filter((part) => /^\p{L}/u.test(part));
  const initials = words.map((part) => part[0]).join('').slice(0, 2).toUpperCase();
  const { background, tone } = monogramTone(initials);
  return (
    <span className="ws-monogram" data-tone={tone} style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background }} aria-hidden="true">
      {initials || <UserRound size={Math.round(size * 0.48)} strokeWidth={2} />}
    </span>
  );
}

const STATUS_LABELS = { live: 'Live', paused: 'Paused', draft: 'Draft', failed: 'Failed', warning: 'Warning', info: 'Scheduled' };

export function Status({ tone = 'draft', children }) {
  return <span className={`ws-status ws-status--${tone}`}>{children || STATUS_LABELS[tone] || tone}</span>;
}

export function Skeleton({ width = '100%', height = 14, radius = 8, style }) {
  return <span className="ws-skeleton" style={{ display: 'block', width, height, borderRadius: radius, ...style }} aria-hidden="true" />;
}

export function Kpi({ label, value, hint, icon: Icon, loading }) {
  return (
    <div className="ws-kpi">
      <span className="ws-kpi-label">{Icon && <Icon size={14} aria-hidden="true" />}{label}</span>
      {loading ? <Skeleton width={80} height={26} /> : <span className="ws-kpi-value">{value}</span>}
      {hint && !loading && <span className="ws-muted" style={{ fontSize: 12 }}>{hint}</span>}
    </div>
  );
}

export function WsDialog({ title, description, children, onClose, labelledBy = 'ws-dialog-title', wide = false }) {
  const panelRef = useRef(null);
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') { event.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    panelRef.current?.querySelector('input, textarea, button:not([data-close])')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="ws-overlay ws-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div ref={panelRef} className="ws-dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy} style={wide ? { width: 'min(560px, 100%)' } : undefined}>
        <h2 id={labelledBy} className="ws-h2">{title}</h2>
        {description && <p className="ws-muted" style={{ marginTop: 6, fontSize: 14 }}>{description}</p>}
        <div style={{ marginTop: 18 }}>{children}</div>
      </div>
    </div>
  );
}

export const firstName = (name = '') => String(name).trim().split(/\s+/)[0] || '';

export function greetingFor(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export const formatNumber = (value) => (Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value).toLocaleString('en-IN') : '—');

export const percent = (part, whole) => (whole ? Math.round((Number(part) / Number(whole)) * 100) : 0);

export function formatTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s?(am|pm)$/i, (m) => ` ${m.trim().toUpperCase()}`);
}

export function formatDay(value, now = new Date()) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((startOf(date) - startOf(now)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

export const formatWhen = (value) => (value ? `${formatDay(value)}, ${formatTime(value)}` : '—');

export function relativeTime(value, now = Date.now()) {
  const time = value ? new Date(value).getTime() : NaN;
  if (!Number.isFinite(time)) return '';
  const minutes = Math.round((now - time) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return formatDay(value);
}
